
import { useState } from 'react';

export default function ROICalculator() {
  const [formData, setFormData] = useState({
    reductionMinutes: 30,
    dailyChanges: 5,
    workingDays: 250,
    hourlyLabor: 4000
  });

  const [results, setResults] = useState({
    laborROI: 0,
    totalROI: 0
  });

  const calculateROI = () => {
    const dailyHoursSaved = (formData.reductionMinutes * formData.dailyChanges) / 60;
    const laborROI = dailyHoursSaved * formData.hourlyLabor * formData.workingDays;
    const totalROI = laborROI;

    setResults({
      laborROI,
      totalROI
    });
  };

  const handleInputChange = (field: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <section id="roi" className="py-20 bg-gradient-to-br from-yellow-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            【たった30秒のROI計算】
          </h2>
          <p className="text-xl text-gray-600">
            御社の数字で具体的な改善効果を確認
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-yellow-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">計算条件を入力</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  1回の交換短縮時間（分）
                </label>
                <input
                  type="number"
                  value={formData.reductionMinutes}
                  onChange={(e) => handleInputChange('reductionMinutes', Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  1日の交換回数
                </label>
                <input
                  type="number"
                  value={formData.dailyChanges}
                  onChange={(e) => handleInputChange('dailyChanges', Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  年間稼働日数
                </label>
                <input
                  type="number"
                  value={formData.workingDays}
                  onChange={(e) => handleInputChange('workingDays', Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  min="1"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  人件費フルコスト（円/時間）
                </label>
                <input
                  type="number"
                  value={formData.hourlyLabor}
                  onChange={(e) => handleInputChange('hourlyLabor', Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  min="1000"
                  step="1000"
                />
              </div>

              <button
                onClick={calculateROI}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 py-4 rounded-lg font-semibold text-lg transition-colors cursor-pointer"
              >
                ROIを計算する
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg border border-yellow-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">年間改善効果</h3>
            
            <div className="space-y-6">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-2">
                  人件費削減効果
                </h4>
                <p className="text-3xl font-bold text-gray-600">
                  ¥{results.laborROI.toLocaleString()}
                </p>
                <p className="text-sm text-gray-700 mt-2">
                  削減時間 × 人件費フルコスト × 稼働日
                </p>
              </div>

              <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-gray-900 rounded-lg p-6">
                <h4 className="text-xl font-semibold mb-2">
                  合計年間効果
                </h4>
                <p className="text-4xl font-bold">
                  ¥{results.totalROI.toLocaleString()}
                </p>
                <p className="text-sm mt-2 opacity-80">
                  ※不良・再加工・監査対応コスト削減は上乗せ効果
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800">
                  <strong>注意：</strong>この計算は基本的な時間短縮効果のみです。品質改善による不良削減、監査対応時間短縮、教育コスト削減などの追加効果は含まれていません。
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 text-center">
          <div className="bg-white rounded-lg p-6 shadow-lg inline-block border border-yellow-200">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              詳細なROI分析をご希望の方
            </h4>
            <p className="text-gray-600 mb-4">
              御社の具体的な条件でより詳細な効果試算を行います
            </p>
            <button className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 px-8 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap">
              詳細ROI分析を依頼
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
